import { attachEvent, requestHandler } from '../../js/tools';
import '../browser-streaming';
import '../form/input-statefull';
import '../form/button-stateful';
import ButtonStateful from '../form/button-stateful';
import InputStatefull, { INPUT_STATE } from '../form/input-statefull';
import fEvents from '../../js/events';
import { animateEnter, animateLeave, isEmailValid, isPasswordValid } from '../../tools';
import BrowserStream from '../browser-streaming';

export enum RealTimeEventTypes {
    BROWSER_STREAM = 'browser_stream'
}

export default class LinkedinAuth extends HTMLElement {
    private csrfToken: string;
    private memberId: string;

    constructor() {
        super();
        this.memberId = this.getAttribute('member-id');
        this.attachShadow({ mode: 'open' });
        attachEvent('.linkedin__login', 'click', () => this._onLinkedInLogin(), this.shadowRoot);
        attachEvent('input-statefull', 'value-changed', e => this._onLinkedInDataChanged(e), this.shadowRoot);
        attachEvent('.password__icon', 'click', () => this.onTogglePasswordVisibility(), this.shadowRoot);
        attachEvent('#linkedinData_password', 'keyup', (e: any) => {
            const details = {
                container: e.target,
                detail: { value: e.target.value }
            };

            this._onLinkedInDataChanged(details);
        }, this.shadowRoot);
        attachEvent('.login__restart__link', 'click', _ => {
            this.clearSession();
            this.hideLoader();
        }, this.shadowRoot);
        attachEvent('.policy', 'click', _ => {
            this.tooglePrivacyPolicy();
        }, this.shadowRoot);

        attachEvent('browser-stream', 'session-ended', _ => {
            this.clearSession();
            this.hideLoader();
        }, this.shadowRoot);

    }

    connectStreaming(csrfToken: string) {
        const browserStreaming = this.shadowRoot.querySelector('browser-stream');
        browserStreaming.setAttribute('csrftoken', csrfToken);
    }

    tooglePrivacyPolicy = () => {
        const policiesCheckbox = this.shadowRoot.querySelectorAll('.policy');
        // check if all policies are checked
        const isAllChecked = Array.from(policiesCheckbox).every((e: any) => e.checked);
        if (isAllChecked) {
            this.enableLoginButton();
        } else {
            this.disableLoginButton();
        }
    };

    connectedCallback() {
        this.shadowRoot.innerHTML = this.renderAssests();
        setTimeout(() => {
            this.shadowRoot.innerHTML += this.render();
            this.tooglePrivacyPolicy();
        }, 50);

    }

    // observe attribute changes
    static get observedAttributes() {
        return ['email', 'password'];
    }

    private get elements () {
        return {
            emailInput: this.shadowRoot.querySelector('#linkedInData_email') as InputStatefull,
            passwordInput: this.shadowRoot.querySelector('#linkedinData_password') as HTMLInputElement,
            loginButton: this.shadowRoot.querySelector('.linkedin__login') as ButtonStateful,
            browser: this.shadowRoot.querySelector('browser-stream') as BrowserStream
        };
    }

    attributeChangedCallback(name, oldVal, newVal) {
        if (oldVal === newVal) {
            return;
        }

        this.shadowRoot.innerHTML = this.render();
    }

    _showMessage(messageText) {
        fEvents.dispatch(this, 'show-message', { messageText });
    }

    async _onLinkedInLogin() {
        const loginButton: ButtonStateful = this.elements.loginButton;
        const body = {} as any;

        const email = (this.shadowRoot.querySelector('#linkedInData_email') as InputStatefull).getValue();
        const password = (this.shadowRoot.querySelector('#linkedinData_password') as HTMLInputElement).value;

        body.email = email;
        body.password = password;

        if (!this.csrfToken) {
            const newCsrfToken = await this._getCSRFToken();
            this.csrfToken = newCsrfToken;
        }

        if (!isEmailValid(email) && !(isPasswordValid(password))) {
            loginButton.setAttribute('icon', 'cancel');
            this._showMessage('Please enter valid Linkedin email and password');
            return;
        }

        this.showLoader();

        loginButton
            .classList.remove('linkedin__login--failed');
        loginButton
            .classList.remove('linkedin__login--success');

        if (this.memberId) { // We use this component when we sign up for the new member, but also to reconnect the existing member. In the second case, we need to pass the memberId to know which member to attach the result to
            body.memberId = this.memberId;
        }

        body.csrfToken = this.csrfToken;

        const url = '/v2/linkedin/login';
        this.showDisplayMessage('');
        this.saveCredentials(email, password);
        this.connectStreaming(this.csrfToken);
        requestHandler({
            url,
            payload: JSON.stringify(body),
            method: 'POST',
            isJson: true,
        }).then((r: any) => {
            this._playNotificationSound();
            const result = JSON.parse(r.data);
            this.csrfToken = result.csrfToken;
            if (result.success) {
                this.elements.browser.endSession();
                this.disableLoginButton();
                loginButton.setAttribute('text', 'Login Successful, Redirecting, please wait...');
                loginButton.setAttribute('icon', 'check_circle');

                fEvents.dispatch(this, 'auth-success', {
                    sessionCookie: result.sessionCookie,
                    browserCookie: result.browserCookie,
                    email: result?.credentials?.email || email,
                    password: result?.credentials?.password || password
                });

                if (result?.credentials?.email && result?.credentials?.password) {
                    this.saveCredentials(result?.credentials?.email, result?.credentials?.password);
                }
            } else {
                console.warn('Error during login, probably timeout', result);
            }
        }).catch(err => {
            console.log('An overall internal server error, how do you handle this?', { err });
            this.onLoginError(err?.message || 'An error occurred, please try again');
            this.elements.browser.endSession();
        }).finally(() => {
            loginButton.setAttribute('state', 'none');
        });
    }

    onLoginError = (message: string) => {
        this._showMessage(message);
        this.showDisplayMessage(message);

        const origin = new URL(window.location.href).origin;
        console.warn(`Login failed with an error ${message} \n Session recordings available at ${origin}/v2/files/recordings/${this.csrfToken}`);
        this.clearSession();
    };

    clearSession = () => {
        this.csrfToken = ''; // Clear csrfToken so server generates it
        this.hideLoader();

        this.elements.loginButton
            .classList
            .add('linkedin__login--failed');

        this.elements.loginButton.setAttribute('text', 'Login');
        this.elements.loginButton.setAttribute('state', 'none');
    };

    async _getCSRFToken() {
        return requestHandler({
            url: '/v2/linkedin/csrf-token',
            method: 'GET'
        }).then(r => {
            const result = JSON.parse(r.data);
            return result.csrfToken;
        });
    }

    _playNotificationSound() {
        const audio: HTMLAudioElement = this.shadowRoot.getElementById('notification') as HTMLAudioElement;
        audio?.play();
    }

    private showDisplayMessage(message: string) {
        if (!message) return this.shadowRoot.querySelector('.linkedin__error').classList.add('linkedin__error--hidden');
        const errorContainer = this.shadowRoot.querySelector('.linkedin__error');
        const htmlMessage = message.replace(/(?:\r\n|\r|\n)/g, '<br />');
        this.shadowRoot.querySelector('.linkedin__error__message').innerHTML = htmlMessage;
        errorContainer.classList.remove('linkedin__error--hidden');
    }

    private saveCredentials(email: string, password: string) {
        fEvents.dispatch(this, 'save-auth-credentials', { email, password });
    }

    private enableLoginButton() {
        this.elements.loginButton.removeAttribute('disabled');
    }

    private disableLoginButton() {
        this.elements.loginButton.setAttribute('disabled', 'true');
    }

    _onLinkedInDataChanged(e) {
        const inputField = e.container;

        const inputName = inputField.getAttribute('name');
        let payload = {};

        if (inputName === 'linkedInData_email' && isEmailValid(e.detail.value)) {

            payload = { email: e.detail.value };

        } else if (
            inputName === 'linkedinData_password' && isPasswordValid(e.detail.value)) {

            payload = { password: e.detail.value };

        } else {
            inputField
                .setAttribute('state', INPUT_STATE.none);

            return null;
        }

        inputField.setAttribute('state', INPUT_STATE.ok);
        fEvents.dispatch(this, 'login-details-changed', payload);
    }

    hideLoader = () => {
        this.showLoginForm();
    };

    showLoader = () => {
        this.hideLoginForm();
    };

    showLoginForm = () => {
        const loginForm = this.shadowRoot.querySelector('.login-form');
        animateEnter(loginForm);
    };

    hideLoginForm = () => {
        const loginForm = this.shadowRoot.querySelector('.login-form');
        animateLeave(loginForm);
    };

    onTogglePasswordVisibility = () => {
        const passwordInput = this.shadowRoot.querySelector('#linkedinData_password') as HTMLInputElement;
        const passwordIcon = this.shadowRoot.querySelector('.password__icon') as HTMLElement;
        const isPasswordVisible = passwordIcon.innerText === 'visibility';

        if (isPasswordVisible) {
            passwordInput.classList.remove('text-security');
            passwordIcon.innerHTML = 'visibility_off';
        } else {
            passwordInput.classList.add('text-security');
            passwordIcon.innerHTML = 'visibility';
        }
    };

    private renderAssests = () => {
        return `
            <link rel="stylesheet" href="https://fonts.googleapis.com/icon?family=Material+Icons">
            <link rel="stylesheet" href="/styles/index.css">
            <link rel="stylesheet" async href="/styles/linkedin-auth.css">
            <audio id="notification">
                <source src="/assets/sounds/notification.wav" type="audio/wav">
            </audio>
        `;
    };

    render = () => {
        const [email, password] = ['email', 'password'].map(a => this.getAttribute(a));

        return `
        <style>
            #linkedinData_password { border: 1px solid black; font-size: 18px; border-radius: 8px; padding: 17px 22px; width: 100%; box-shadow: none; cursor: text; }
            .text-security { -webkit-text-security: disc; text-security: disc; }
            .password__container { position: relative; }
            .password__icon { position: absolute; right: 18px; cursor: pointer; }
            .browser-streaming__container { display: flex; justify-content: center; }

            @media (max-height: 750px) {
               .login-form.hidden + .browser-streaming__container { padding-top: 200px; }
            }
        </style>

        <div class="login-form">
            <div class="login-form__section">
                <div class="fl-textfield-wrapper">
                        <input-statefull
                                type="email"
                                name="linkedInData_email"
                                id="linkedInData_email"
                                inputstyle='border: 1px solid black;height: 60px;font-size: 18px;border-radius: 8px;padding: 22px;width: 100%;box-shadow: none;'
                                state="${email ? 'ok' : 'none'}"
                                placeholder="Fill your LinkedIn Email"
                                value="${email || ''}" />
                </div>
                <div class="fl-textfield-wrapper password__container">
                    <input type="text" class="fl-textfield text-security" autocomplete="off" id="linkedinData_password" name="linkedinData_password" placeholder="Fill your LinkedIn password" value="${password || ''}" />
                    <i class="material-icons password__icon">visibility</i>
                </div>

                <div class="confirm-privacy">
                  <input type="checkbox" class="policy" id="privacyPolicy" name="privacyPolicy" value="privacyPolicy"/>
                  <label for="privacyPolicy">By checking this you agree to our <a href="/privacy" target="_blank">Privacy Policy</a></label>
                </div>
                <div class="confirm-privacy">
                    <input type="checkbox" class="policy" id="communityPolicy" name="communityPolicy" value="communityPolicy"/>
                    <label for="communityPolicy">I agree to support group members by giving likes from my LinkedIn account</label>
                </div>

                <button-stateful height='40px'  buttonstyle="width: 100%;height: 55px;background: #3836BE;color: white;border-radius: 12px;font-size: 18px;" width='fit-content' minwidth='200px' loadingtext="Please wait... this can take a few minutes, we\'re logging into your LinkedIn account" textalign='center' rounded='8px' text="Login" class="linkedin__login"></button-stateful>
            </div>

            <div class="linkedin__error linkedin__error--hidden">
                <span class="material-icons linkedin__error__icon">contact_support</span>
                <span class="linkedin__error__message">There was an error Logginin in, signin in with one-time link</span>
            </div>
            </div>
            </div>


            <div class="browser-streaming__container">
               <browser-stream></browser-stream>
            </div>
        </div>
    `;
    };

}

customElements.define('linkedin-auth', LinkedinAuth);
